import type { FunctionComponent } from 'preact';

import type { TemplateFunction } from 'Components/types';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index';
import { productDataSelector } from 'Core/selectors/product';
import { useSelector } from 'Core/store';
import type { SelectionEntry } from 'Models/index';
import { getUriFromRequest } from 'Modules/converter/index';
import type { ServerModel, ServerValue } from 'Modules/serverApi/types';

type GetSearchLinkProps = {
  facetSelection?: SelectionEntry[];
  customHref?: string;
};

type GetSearchLinkArgs = GetSearchLinkProps & {
  withVehicle?: boolean;
};

type ProductDataType = {
  field: string;
  value: ServerValue;
}[];

type Params = {
  productData: ServerModel.SearchItem | ProductDataType;
  getSearchLink: (props: GetSearchLinkProps) => string;
};

type Props = {
  template: TemplateFunction<Params>;
  fields?: string[];
};

const ProductData: FunctionComponent<Props> = ({ template, fields }) => {
  const productData = useSelector(productDataSelector);

  const productColors = productData?._Color;
  const colorSelection = productColors ? productColors.toFacetValues('_Color') : [];
  const vehicleSelection = useSelector(createSelectedVehicleSelectionSelector);

  if (!productData || !Object.keys(productData).length) {
    return null;
  }

  const getSearchLink = ({ facetSelection = [], customHref, withVehicle = true }: GetSearchLinkArgs) => {
    const selection = [...facetSelection, ...colorSelection, ...(withVehicle ? vehicleSelection : [])].filter(
      Boolean,
    );

    const {
      pathname: defaultPathname,
      search,
      hash,
    } = getUriFromRequest({ selection }, { goToSearchPage: true });

    return `${customHref || defaultPathname}${search}${hash}`;
  };

  const data = fields?.length
    ? Object.entries(productData)
        .filter(([field]) => fields.includes(field))
        .map(([field, value]) => ({ field, value }))
    : productData;

  return template.call({ productData: data, getSearchLink });
};

export default ProductData;
